import { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { FastForward, MapPin, Sparkles, User, Zap } from "lucide-react";
import { StockTicker } from "@/components/ticker";
import { FeedFilters, FilterModal, NearbyStrip, VenueStrip } from "@/components/restaurant";
import { ConsentNotice } from "@/components/account/ConsentNotice";
import { RatingPrompt } from "@/components/account/RatingPrompt";
import { SignupNudge } from "@/components/account/SignupNudge";
import { LanguageToggle } from "@/components/i18n/LanguageToggle";
import { useGeolocation } from "@/hooks/useGeolocation";
import { useDwellDetection } from "@/hooks/useDwellDetection";
import { recordVisit } from "@/lib/visits";
import { useAutoVisits } from "@/lib/autoVisits";
import { useAuthStore } from "@/services/authStore";
import { useShowcase, useViewerSignedIn } from "@/services/showcase";
import { useTaxonomy } from "@/data/TaxonomyProvider";
import { useTranslation } from "@/i18n/I18nProvider";
import { cityName, citySlug, isCityId } from "@/lib/cities";
import { siteUrl, usePageMeta } from "@/lib/pageMeta";
import { parsePlaceParam, venuePath, venueSlugOf } from "@/lib/venueUrl";
import { useReportStore } from "@/services/reportStore";
import NotFound from "./NotFound";
import { buildTickerFeed } from "@/lib/buildTickerFeed";
import { isEveningInRomania } from "@/lib/clock";
import { cn } from "@/lib/cn";
import { clearIntentKey, toggleToken, type IntentKey } from "@/lib/feedFilter";
import { looksAskable } from "@/lib/nlSearch";
import { FeedGrid } from "./home/FeedGrid";
import { GeoVisitToast } from "./home/GeoVisitToast";
import { HomeTabs, type HomeTab } from "./home/HomeTabs";
import { useAccountPanel } from "./home/useAccountPanel";
import { useFeedData } from "./home/useFeedData";
import { useFeedSearch } from "./home/useFeedSearch";
import { usePersonalization } from "./home/usePersonalization";
import { useRankedFeed } from "./home/useRankedFeed";
import { useVenueDetail } from "./home/useVenueDetail";
import { VenueDetailModal } from "./home/VenueDetailModal";

// Lazy so the MapLibre bundle only downloads on the first tap of the map
// button — the feed's first paint stays map-free.
const MapView = lazy(() => import("@/components/restaurant/MapView"));

// Same treatment for the account sidebar: the whole auth UI is a separate
// chunk that downloads on the first tap of the account button.
const AccountSidebar = lazy(() => import("@/components/account/AccountSidebar"));

// The city feed. State and derivations live in the `./home/` hooks (data,
// personalization, search, ranking, detail, account panel); this file wires
// them to the page layout.
export default function Home() {
  const { t, lang } = useTranslation();
  const { cuisines, tags } = useTaxonomy();
  // City comes from the path (`/cluj` → cluj-napoca) on the canonical routes, or
  // the legacy `?city=` query param on `/app` (kept working for back-compat +
  // auth redirects). Unknown/missing → null → redirect to the gate below.
  // Inside the `/showcase-cluj` sales demo (docs/features/showcase.md) the
  // city, venues, location and live capacity are all local and simulated, and
  // the account surfaces are left out.
  // One path segment is a city (`/cluj`) or a venue page (`/cluj-pomet`,
  // src/lib/venueUrl.ts): the venue page is this same feed with that venue's
  // detail open.
  const showcase = useShowcase();
  const navigate = useNavigate();
  const { citySlug: citySlugParam } = useParams();
  const [searchParams] = useSearchParams();
  const cityParam = searchParams.get("city");
  const place = showcase || !citySlugParam ? null : parsePlaceParam(citySlugParam);
  const city = showcase
    ? showcase.cityId
    : citySlugParam
      ? (place?.cityId ?? null)
      : isCityId(cityParam)
        ? cityParam
        : null;
  const venueSlug = place?.venueSlug ?? null;
  const { restaurants, events, discountsByRestaurant, loading, loadError, reload } =
    useFeedData(city);
  const [tab, setTab] = useState<HomeTab>("recommended");
  const { selectedRestaurant, detailRef, openedVenues, selectRestaurant, openFromUrl, closeDetail } =
    useVenueDetail({
      // Leaving a venue page that the visit started on lands in its city feed.
      onDeepClose: () => city && navigate(`/${citySlug(city)}`, { replace: true }),
    });
  // A venue URL opens that venue once the city's venues are in; an unknown or
  // hidden one is a 404.
  const deepVenue = venueSlug ? (restaurants.find((r) => venueSlugOf(r) === venueSlug) ?? null) : null;
  const deepOpened = useRef<string | null>(null);
  useEffect(() => {
    if (!deepVenue || deepOpened.current === deepVenue.id) return;
    deepOpened.current = deepVenue.id;
    openFromUrl(deepVenue);
    // openFromUrl is a fresh closure each render; the id guard makes this run once per venue.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepVenue]);
  const venueMissing = venueSlug !== null && !loading && !loadError && deepVenue === null;

  // Visitor location for the "Closest to you" strip (tap-to-enable).
  const deviceGeo = useGeolocation({ mode: showcase ? "off" : "auto" });
  const geo = showcase?.geo ?? deviceGeo;
  // A passive geo-dwell conversion surfaces as a dismissible toast.
  const [geoVisitToast, setGeoVisitToast] = useState<{ visitId: number; name: string } | null>(null);

  // Live map — `mounted` sticks after the first open so the lazy chunk loads
  // once and re-opens are instant; `open` drives the sheet's enter/exit.
  const [mapOpen, setMapOpen] = useState(false);
  const [mapMounted, setMapMounted] = useState(false);

  const account = useAccountPanel();
  // Guests get the account-gated view: live POS capacity masked (RestaurantCard
  // handles that itself) and the Offers tab locked behind a free account.
  const signedIn = useViewerSignedIn();
  const { myVisits, similarity, similarityPending } = usePersonalization(signedIn);
  const accountInitial = useAuthStore(
    (s) => s.consumerProfile?.display_name?.trim().charAt(0).toUpperCase() ?? null,
  );

  // A "Report" tap anywhere (venue page, offer card, Offers popup): close the
  // venue page and open the sidebar's report box (docs/features/reports.md).
  const reportNonce = useReportStore((s) => s.nonce);
  const lastReportNonce = useRef(reportNonce);
  useEffect(() => {
    if (reportNonce === lastReportNonce.current) return;
    lastReportNonce.current = reportNonce;
    closeDetail();
    account.openReport();
    // closeDetail / account are fresh closures each render; the nonce is the trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportNonce]);

  // Tab title + description: the venue's while its page is open, else the city's.
  const metaVenue = showcase ? null : selectedRestaurant;
  const metaVenuePath = metaVenue ? venuePath(metaVenue.city, venueSlugOf(metaVenue)) : null;
  usePageMeta(
    showcase || !city
      ? null
      : metaVenue && metaVenuePath
        ? {
            title: t("META_VENUE_TITLE", { name: metaVenue.name, city: cityName(city) }),
            description: t("META_VENUE_DESC", { name: metaVenue.name, city: cityName(city) }),
            canonical: siteUrl(metaVenuePath),
          }
        : {
            title: t("META_CITY_TITLE", { city: cityName(city) }),
            description: t("META_CITY_DESC", { city: cityName(city) }),
            canonical: siteUrl(`/${citySlug(city)}`),
          },
  );

  const search = useFeedSearch({ cuisines, tags, signedIn, coords: geo.coords });

  // Passive conversion capture — a 10-min foreground dwell at an opened venue
  // records a `geo` visit (reuses the feed's location; no extra prompt). Opt-in:
  // off until the diner turns on "Count my visits automatically" in Settings.
  const autoVisits = useAutoVisits();
  useDwellDetection({
    enabled: autoVisits && !showcase,
    coords: geo.coords,
    accuracy: geo.accuracy,
    restaurants,
    isOpened: (id) => openedVenues.current.has(id),
    onDwell: (venueId) => {
      if (!geo.coords) return;
      void recordVisit(venueId, "geo", geo.coords, geo.accuracy).then((visit) => {
        if (visit) {
          setGeoVisitToast({
            visitId: visit.id,
            name: restaurants.find((r) => r.id === venueId)?.name ?? "",
          });
        }
      });
    },
  });

  const { now, orderedRestaurants, recommendations, fastVenues, topChips, visibleRestaurants } =
    useRankedFeed({
      restaurants,
      discountsByRestaurant,
      signedIn,
      myVisits,
      similarity,
      cuisines,
      tags,
      lang,
      query: search.query,
      activeFilters: search.activeFilters,
      intent: search.intent,
      coords: geo.coords,
    });

  const builtTicker = useMemo(
    () => buildTickerFeed(events, restaurants),
    [events, restaurants],
  );
  const tickerFeed = showcase?.tickerEvents ?? builtTicker;

  const { hasFilters } = search;

  // A path segment that is neither a city nor a venue page, or a venue page
  // whose venue is gone: the custom 404. No city at all (`/app`) → the gate.
  if ((citySlugParam && !showcase && !place) || venueMissing) return <NotFound />;
  if (!city) return <Navigate to="/" replace />;
  const gatePath = showcase?.basePath ?? "/";
  const cityLink = (
    <Link
      to={gatePath}
      className="inline-flex items-center gap-1.5 rounded-full border border-white/[0.12] light:border-black/10 bg-zinc-900/70 light:bg-white/70 px-2.5 py-1 text-xs font-medium text-zinc-300 light:text-zinc-700 transition hover:border-white/30 light:hover:border-black/30 hover:text-zinc-100 light:hover:text-zinc-900"
    >
      <MapPin className="h-3 w-3 text-dim light:text-zinc-600" aria-hidden />
      {cityName(city)}
      <span className="text-zinc-600">· {t("HOME_CHANGE_CITY")}</span>
    </Link>
  );

  return (
    <main className="app-ambient min-h-screen bg-background text-foreground">
      {/* Fixed top-right cluster: account button + language pill. On phone they
          stack vertically (En/Ro on top, account under it) so the account icon
          no longer sits beside the pill and crowds the city button; on ≥sm they
          sit in a row (account left, pill right) as before. `flex-col-reverse`
          keeps the DOM order [account, toggle] while rendering the toggle on top. */}
      <div
        className="fixed right-3 z-50 flex flex-col-reverse items-end gap-2 sm:right-5 sm:flex-row sm:items-center"
        style={{ top: "max(0.75rem, env(safe-area-inset-top, 0px))" }}
      >
        {!showcase && (
          <button
            type="button"
            onClick={account.openAccount}
            aria-label={t("ACCOUNT_BUTTON")}
            aria-haspopup="dialog"
            className={cn(
              "grid h-10 w-10 place-items-center rounded-full border shadow-[0_0_0_1px_rgba(0,0,0,0.7)] light:shadow-none backdrop-blur transition sm:h-9 sm:w-9",
              accountInitial
                ? "border-white/20 light:border-black/15 bg-paper light:bg-ink font-display text-sm text-ink light:text-paper hover:bg-white light:hover:bg-zinc-800"
                : "border-white/15 light:border-black/15 bg-zinc-950/90 light:bg-white/90 text-zinc-300 light:text-zinc-700 hover:border-white/40 light:hover:border-black/35 hover:text-zinc-50 light:hover:text-zinc-900",
            )}
          >
            {accountInitial ?? <User className="h-4 w-4" aria-hidden />}
          </button>
        )}
        <LanguageToggle inline />
      </div>
      <StockTicker events={tickerFeed} forceMotion={showcase !== null} />

      <section className="container py-6 sm:py-10">
        <header className="flex flex-wrap items-end justify-between gap-3 sm:gap-4">
          {/* Reserve a little room on the right for the fixed control cluster so
              the city pill doesn't slide under it. The cluster is now a narrow
              vertical stack on phone, so a small reserve is enough — the title
              renders on one line and the subtitle on two. */}
          <div className="min-w-0 pr-16 sm:pr-0">
            {showcase ? (
              // Honest even if the demo link travels: occupancy here is simulated.
              <div className="flex flex-wrap items-center gap-2">
                {cityLink}
                <span className="inline-flex items-center rounded-full border border-amber-400/30 light:border-amber-600/30 bg-amber-500/10 light:bg-amber-50/90 px-2.5 py-1 font-mono text-3xs font-medium uppercase tracking-[0.14em] text-amber-200 light:text-amber-800">
                  {t("SHOWCASE_DEMO_PILL")}
                </span>
                {/* Speeds up the simulated venues (not the ticker) to show change faster. */}
                <button
                  type="button"
                  onClick={showcase.cycleSpeed}
                  aria-label={t("SHOWCASE_SPEED_LABEL", { speed: showcase.speed })}
                  className={cn(
                    "inline-flex items-center gap-1 rounded-full border px-2.5 py-1 font-mono text-3xs font-medium uppercase tracking-[0.14em] tabular-nums transition",
                    showcase.speed > 1
                      ? "border-pulse/50 bg-pulse/10 text-pulse"
                      : "border-white/[0.12] light:border-black/10 bg-zinc-900/70 light:bg-white/70 text-zinc-300 light:text-zinc-700 hover:border-white/30 light:hover:border-black/30",
                  )}
                >
                  <FastForward className="h-3 w-3" aria-hidden />
                  {showcase.speed}×
                </button>
              </div>
            ) : (
              cityLink
            )}
            <h1 className="mt-3 font-display text-[1.9rem] leading-[1.02] tracking-tight text-zinc-50 light:text-zinc-900 sm:text-[2.6rem]">
              {t(isEveningInRomania(now) ? "HOME_TITLE" : "HOME_TITLE_DAY")}
            </h1>
            <p className="mt-2.5 max-w-md text-pretty text-sm leading-relaxed text-zinc-300 light:text-zinc-700">{t("HOME_SUBTITLE")}</p>
          </div>
        </header>

        <HomeTabs tab={tab} onChange={setTab} signedIn={signedIn} />

        {/* "Recommended for you" — signed-in only, hidden entirely for guests
            (no lock panel; new account-gated surfaces fail toward the guest
            view). Skeletons while the feed or visit history load; the engine's
            fallback guarantees content whenever the city has venues. */}
        {tab === "recommended" && !hasFilters && signedIn && (loading || orderedRestaurants.length > 0) && (
          <VenueStrip
            title={t("RECS_TITLE")}
            icon={<Sparkles className="h-4 w-4 text-pulse" aria-hidden />}
            subtext={recommendations.personalized ? t("RECS_SUBTEXT") : undefined}
            items={recommendations.items}
            discountsByRestaurant={discountsByRestaurant}
            loading={loading || myVisits === null || similarityPending}
            onSelect={(r) => selectRestaurant(r, "recommended")}
          />
        )}

        {/* "Closest to you" — Recommended tab, default view (hidden while the
            visitor is searching/filtering). Visible from first paint: shows
            skeletons while the feed loads or location resolves, then ranks open
            venues by distance from the visitor's geolocation. */}
        {tab === "recommended" && !hasFilters && (loading || orderedRestaurants.length > 0) && (
          <NearbyStrip
            restaurants={orderedRestaurants}
            discountsByRestaurant={discountsByRestaurant}
            geo={geo}
            loading={loading}
            onSelect={(r) => selectRestaurant(r, "nearby")}
            onOpenMap={() => {
              setMapMounted(true);
              setMapOpen(true);
            }}
          />
        )}

        {/* "Want something quick?" — §28 fast venues, visible to everyone.
            No skeleton phase: while zero venues carry the flag (or the column
            hasn't been migrated yet) the WHOLE section — header included —
            renders nothing, instead of skeletons that flash and vanish. */}
        {tab === "recommended" && !hasFilters && !loading && fastVenues.length > 0 && (
          <VenueStrip
            title={t("QUICK_TITLE")}
            icon={<Zap className="h-4 w-4 text-pulse" aria-hidden />}
            subtext={t("QUICK_SUBTEXT")}
            items={fastVenues}
            discountsByRestaurant={discountsByRestaurant}
            onSelect={(r) => selectRestaurant(r, "quick")}
          />
        )}

        {/* Search + filters — Recommended tab only, once the feed has loaded. */}
        {tab === "recommended" && !loading && orderedRestaurants.length > 0 && (
          <FeedFilters
            query={search.query}
            onQueryChange={search.changeQuery}
            recommended={topChips}
            active={search.activeFilters}
            onToggle={(token) => search.setActiveFilters((prev) => toggleToken(prev, token))}
            onClear={search.clearAllFilters}
            onOpenCustom={() => search.setFilterOpen(true)}
            resultCount={visibleRestaurants.length}
            intent={search.intent}
            onIntentRemove={(key: IntentKey) => search.setIntent((prev) => clearIntentKey(prev, key))}
            askable={looksAskable(search.query)}
            askMissed={search.askMissed}
            askPending={search.askPending}
            askByAi={search.askByAi}
            onAsk={() => void search.runAsk()}
          />
        )}

        <FeedGrid
          city={city}
          tab={tab}
          signedIn={signedIn}
          loading={loading}
          loadError={loadError}
          onRetry={reload}
          restaurants={restaurants}
          orderedCount={orderedRestaurants.length}
          visibleRestaurants={visibleRestaurants}
          discountsByRestaurant={discountsByRestaurant}
          now={now}
          hasFilters={hasFilters}
          onClearFilters={search.clearAllFilters}
          onSelect={selectRestaurant}
          onSignUp={account.openSignUp}
        />
      </section>

      <VenueDetailModal
        restaurant={selectedRestaurant}
        discounts={selectedRestaurant ? discountsByRestaurant[selectedRestaurant.id] : undefined}
        geo={showcase ? undefined : geo}
        panelRef={detailRef}
        onClose={closeDetail}
        onLeaveFeedback={
          showcase
            ? undefined
            : () => {
                closeDetail();
                account.openFeedbacks();
              }
        }
        onOpenBonus={
          showcase
            ? undefined
            : () => {
                closeDetail();
                account.openBonus();
              }
        }
      />

      <FilterModal
        open={search.filterOpen}
        onClose={() => search.setFilterOpen(false)}
        active={search.activeFilters}
        onToggle={(token) => search.setActiveFilters((prev) => toggleToken(prev, token))}
        onClear={() => search.setActiveFilters([])}
        resultCount={visibleRestaurants.length}
      />

      {/* Live map sheet (z-[90]) — pin taps feed the same detail modal above. */}
      {mapMounted && city && (
        <Suspense fallback={null}>
          <MapView
            open={mapOpen}
            restaurants={orderedRestaurants}
            discountsByRestaurant={discountsByRestaurant}
            geo={geo}
            cityId={city}
            detailOpen={selectedRestaurant !== null}
            onClose={() => setMapOpen(false)}
            onSelect={(r) => selectRestaurant(r, "map")}
          />
        </Suspense>
      )}

      {/* Account & settings sidebar (z-[110]) — lazy chunk, loads on first open. */}
      {account.accountMounted && !showcase && (
        <Suspense fallback={null}>
          <AccountSidebar
            open={account.accountOpen}
            onClose={account.closeAccount}
            geo={geo}
            initialView={account.accountView}
          />
        </Suspense>
      )}

      {!showcase && (
        <>
          <GeoVisitToast
            toast={geoVisitToast}
            onLeaveFeedback={() => {
              setGeoVisitToast(null);
              account.openFeedbacks();
            }}
            onDismiss={() => setGeoVisitToast(null)}
          />

          {/* Delayed "rate your visit" prompt — self-manages eligibility + throttle. */}
          <RatingPrompt />

          {/* Guest sign-up nudge — once per browser session, defers to the consent
              card on a first-ever visit. Self-manages visibility. */}
          <SignupNudge onSignUp={account.openSignUp} />

          {/* One-time anonymous-ID consent card — self-hides once answered. */}
          <ConsentNotice />
        </>
      )}
    </main>
  );
}
